import React, { CSSProperties, FunctionComponent, useEffect, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import type {
  FormFieldInputType,
  FormFieldMultipleType,
  FormFieldTextareaType,
} from "Shared/components/builders/FormBuilder"
import FormFieldCheckboxWidget from "Shared/components/PuxForm/FormFieldInputCheckboxWidget"
import FormFieldInputWidget from "Shared/components/PuxForm/FormFieldInputWidget"
import FormFieldMultipleWidget from "Shared/components/PuxForm/FormFieldMultipleWidget"
import FormFieldRadioWidget from "Shared/components/PuxForm/FormFieldRadioWidget"
import FormFieldTextareaWidget from "Shared/components/PuxForm/FormFieldTextareaWidget"
import PuxButton from "Shared/components/PuxButton/PuxButton"

type SwatchToken = {
  name: string
  value?: string
  cssVar?: string
  note?: string
}

type SwatchSection = {
  title: string
  description: string
  tokens: SwatchToken[]
}

type TypographySample = {
  name: string
  sourceLabel: string
  sample: string
  family: string
  desktop: string
  mobile: string
  lineHeight: string
  fontWeight: string
  letterSpacing?: string
  note?: string
  liveVars?: string[]
  style: CSSProperties
}

const FIGMA_FILE_URL =
  "https://www.figma.com/design/2xsWwVKVHsXrSchwmUqFLd/Design-System"

const pageStyle: CSSProperties = {
  background:
    "linear-gradient(180deg, var(--color-surface-subtle) 0%, var(--color-surface-default) 24rem)",
  color: "var(--color-text-primary)",
  paddingBottom: "8rem",
}

const sectionStyle: CSSProperties = {
  paddingTop: "5.6rem",
}

const heroPanelStyle: CSSProperties = {
  padding: "clamp(2.4rem, 4vw, 5.6rem)",
  borderRadius: "2.4rem",
  border: "1px solid var(--color-border-subtle)",
  background:
    "radial-gradient(circle at top right, rgba(55, 80, 255, 0.10), transparent 38%), linear-gradient(135deg, var(--color-surface-default), var(--color-surface-soft))",
  boxShadow: "0 2.4rem 6rem rgba(0, 17, 135, 0.08)",
}

const panelStyle: CSSProperties = {
  padding: "2.4rem",
  borderRadius: "2rem",
  border: "1px solid var(--color-border-subtle)",
  backgroundColor: "var(--color-surface-default)",
  boxShadow: "0 1.2rem 3.6rem rgba(0, 17, 135, 0.06)",
}

const sectionHeaderStyle: CSSProperties = {
  display: "grid",
  gap: "1.2rem",
  marginBottom: "2.4rem",
}

const eyebrowStyle: CSSProperties = {
  display: "inline-flex",
  alignSelf: "flex-start",
  padding: "0.6rem 1rem",
  borderRadius: "999px",
  backgroundColor: "var(--color-surface-soft)",
  color: "var(--color-text-secondary)",
  fontSize: "1.2rem",
  fontWeight: 500,
  lineHeight: 1.2,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
}

const badgeStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.8rem",
  padding: "0.8rem 1.2rem",
  borderRadius: "999px",
  border: "1px solid var(--color-border-subtle)",
  backgroundColor: "var(--color-surface-default)",
  color: "var(--color-text-secondary)",
  fontSize: "1.2rem",
  fontWeight: 500,
  lineHeight: 1.2,
}

const swatchGridStyle: CSSProperties = {
  display: "grid",
  gap: "1.6rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(18rem, 1fr))",
}

const swatchCardStyle: CSSProperties = {
  display: "grid",
  gap: "1.2rem",
  padding: "1.6rem",
  borderRadius: "1.6rem",
  backgroundColor: "var(--color-surface-raised)",
  border: "1px solid var(--color-border-subtle)",
}

const typographyGridStyle: CSSProperties = {
  display: "grid",
  gap: "1.6rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(26rem, 1fr))",
}

const typeCardStyle: CSSProperties = {
  ...panelStyle,
  display: "grid",
  gap: "1.6rem",
  minHeight: "100%",
}

const buttonVariantCardStyle: CSSProperties = {
  display: "grid",
  gap: "1.6rem",
  padding: "2rem",
  borderRadius: "1.8rem",
}

const buttonGridStyle: CSSProperties = {
  display: "grid",
  gap: "1.2rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(8.5rem, 1fr))",
}

const buttonCellStyle: CSSProperties = {
  display: "grid",
  alignContent: "start",
  justifyItems: "start",
  gap: "0.8rem",
  minHeight: "8.8rem",
  padding: "1.2rem",
  borderRadius: "1.2rem",
}

const formPreviewGridStyle: CSSProperties = {
  display: "grid",
  gap: "1.6rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(25.6rem, 1fr))",
}

const formThemePanelStyle = (inverse = false): CSSProperties => ({
  display: "grid",
  gap: "2rem",
  padding: "2.4rem",
  borderRadius: "1.8rem",
  border: inverse
    ? "1px solid rgba(255, 255, 255, 0.12)"
    : "1px solid var(--color-border-subtle)",
  backgroundColor: inverse ? "var(--color-secondary)" : "var(--color-surface-raised)",
})

const previewLinkStyle: CSSProperties = {
  color: "var(--color-text-primary)",
  fontSize: "1.4rem",
  fontWeight: 500,
  textDecoration: "none",
  borderBottom: "1px solid rgba(0, 17, 135, 0.16)",
}

const textMetaStyle: CSSProperties = {
  color: "var(--color-text-secondary)",
  fontSize: "1.3rem",
  lineHeight: 1.5,
  margin: 0,
}

const subtleTextStyle: CSSProperties = {
  color: "var(--color-text-muted)",
  fontSize: "1.2rem",
  lineHeight: 1.5,
  margin: 0,
}

const RAW_PALETTE_SECTIONS: SwatchSection[] = [
  {
    title: "Brand Accent",
    description: "Raw blue accent palette exported from Figma variables.",
    tokens: [
      { name: "Accent 100", value: "#D7DAFF" },
      { name: "Accent 200", value: "#BCC5FF" },
      { name: "Accent 200 Alias", value: "#9BA8FF" },
      { name: "Accent 300", value: "#7A8AFF" },
      { name: "Accent Base", value: "#3750FF" },
      { name: "Accent 500", value: "#2535AA" },
      { name: "Accent 600", value: "#1C2880" },
      { name: "Accent 700", value: "#121B55" },
    ],
  },
  {
    title: "Brand Primary",
    description: "Raw yellow brand palette exported from Figma variables.",
    tokens: [
      { name: "Primary 100", value: "#FFF1CC" },
      { name: "Primary 200", value: "#FFE7AA" },
      { name: "Primary 300", value: "#FFCF55" },
      { name: "Primary 400", value: "#FFC22B" },
      { name: "Primary Default", value: "#FFB700" },
      { name: "Primary 500", value: "#AA7A00" },
      { name: "Primary 600", value: "#553D00" },
      { name: "Primary 700", value: "#332500" },
    ],
  },
  {
    title: "Brand Secondary",
    description: "Raw ink palette used for headings and dark surfaces.",
    tokens: [
      { name: "Secondary 100", value: "#CCCFE7" },
      { name: "Secondary 200", value: "#AAB0D7" },
      { name: "Secondary 200 Alias", value: "#8088C3" },
      { name: "Secondary 300", value: "#5560AF" },
      { name: "Secondary Base", value: "#001187" },
      { name: "Secondary Text", value: "#2A399B" },
      { name: "Secondary 500", value: "#000E71" },
      { name: "Secondary 600", value: "#000B5A" },
      { name: "Secondary 700", value: "#000944" },
      { name: "Secondary 800", value: "#00062D" },
    ],
  },
  {
    title: "Brand Tertiary",
    description: "Raw tertiary lavender-neutral support scale.",
    tokens: [
      { name: "Tertiary 100", value: "#F8F9FF" },
      { name: "Tertiary 200", value: "#F5F6FF" },
      { name: "Tertiary 300", value: "#EEF0FF" },
      { name: "Tertiary Base", value: "#EBEDFF" },
      { name: "Tertiary 500", value: "#C4C6D5" },
      { name: "Tertiary 600", value: "#767780" },
      { name: "Tertiary 700", value: "#4E4F55" },
    ],
  },
  {
    title: "Support Colors",
    description: "Support palette from the Figma Colors node.",
    tokens: [
      { name: "Teal 100", value: "#D0F4F0" },
      { name: "Teal Base", value: "#0DA896" },
      { name: "Teal 300", value: "#075E55" },
      { name: "Mint 100", value: "#CCF5E0" },
      { name: "Mint Base", value: "#10B76A" },
      { name: "Mint 300", value: "#0A6640" },
      { name: "Coral 100", value: "#FFE5D6" },
      { name: "Coral Base", value: "#F96030" },
      { name: "Coral 300", value: "#A03500" },
      { name: "Rose 100", value: "#FFD6E0" },
      { name: "Rose Base", value: "#F53B6A" },
      { name: "Rose 300", value: "#800030" },
      { name: "Red 100", value: "#FFE0E3" },
      { name: "Red Base", value: "#F01535" },
      { name: "Red 300", value: "#700018" },
      { name: "Magenta 100", value: "#FAD4F5" },
      { name: "Magenta Base", value: "#CC2DB5" },
      { name: "Magenta 300", value: "#6E1461" },
      { name: "Violet 100", value: "#EDE0FF" },
      { name: "Violet Base", value: "#7C3AED" },
      { name: "Violet 300", value: "#3F1A80" },
      { name: "Indigo 100", value: "#DDE0FF" },
      { name: "Indigo Base", value: "#4F5BDD" },
      { name: "Indigo 300", value: "#242F7A" },
      { name: "Sky 100", value: "#D0EEFF" },
      { name: "Sky Base", value: "#0891D8" },
      { name: "Sky 300", value: "#054F7A" },
    ],
  },
  {
    title: "Neutral",
    description: "Raw neutral grayscale used for layout and utility surfaces.",
    tokens: [
      { name: "Neutral 0", value: "#FFFFFF" },
      { name: "Neutral 50", value: "#F8F9FB" },
      { name: "Neutral 100", value: "#EEF0F4" },
      { name: "Neutral 200", value: "#DCE0E8" },
      { name: "Neutral 300", value: "#BDC3D0" },
      { name: "Neutral 400", value: "#8F97A8" },
      { name: "Neutral 500", value: "#636B7E" },
      { name: "Neutral 600", value: "#474E5E" },
      { name: "Neutral 700", value: "#333845" },
      { name: "Neutral 800", value: "#1F232D" },
      { name: "Neutral 900", value: "#0E1117" },
      { name: "Neutral 1000", value: "#000000" },
    ],
  },
  {
    title: "UI States",
    description: "Raw feedback colors exported from Figma variables.",
    tokens: [
      { name: "Error", value: "#F01535" },
      { name: "Error Light", value: "#FFE0E3" },
      { name: "Info", value: "#0891D8" },
      { name: "Info Light", value: "#D0EEFF" },
      { name: "Notice", value: "#F96030" },
      { name: "Notice Light", value: "#FFE5D6" },
      { name: "Success", value: "#10B76A" },
      { name: "Success Light", value: "#CCF5E0" },
    ],
  },
]

const SEMANTIC_COLOR_SECTIONS: SwatchSection[] = [
  {
    title: "Text and Link Tokens",
    description: "Live CSS variables exported from :root and consumed by typography and links.",
    tokens: [
      { name: "Text Primary", cssVar: "--color-text-primary" },
      { name: "Text Secondary", cssVar: "--color-text-secondary" },
      { name: "Text Tertiary", cssVar: "--color-text-tertiary" },
      { name: "Text Muted", cssVar: "--color-text-muted" },
      { name: "Text Contrast", cssVar: "--color-text-contrast" },
      { name: "White", cssVar: "--color-white" },
      { name: "Link Default", cssVar: "--color-link-default" },
      { name: "Link Hover", cssVar: "--color-link-hover" },
      { name: "Link Active", cssVar: "--color-link-active" },
    ],
  },
  {
    title: "Brand Families",
    description: "Semantic brand aliases currently exposed for implementation work.",
    tokens: [
      { name: "Highlight 100", cssVar: "--color-brand-highlight-100" },
      { name: "Highlight 200", cssVar: "--color-brand-highlight-200" },
      { name: "Highlight 300", cssVar: "--color-brand-highlight-300" },
      { name: "Highlight 400", cssVar: "--color-brand-highlight-400" },
      { name: "Highlight 500", cssVar: "--color-brand-highlight-500" },
      { name: "Primary 100", cssVar: "--color-brand-primary-100" },
      { name: "Primary 200", cssVar: "--color-brand-primary-200" },
      { name: "Primary 300", cssVar: "--color-brand-primary-300" },
      { name: "Primary 400", cssVar: "--color-brand-primary-400" },
      { name: "Primary 500", cssVar: "--color-brand-primary-500" },
    ],
  },
  {
    title: "Surfaces and Borders",
    description: "Live surface layers and border tokens used across cards, sections, and inputs.",
    tokens: [
      { name: "Surface Default", cssVar: "--color-surface-default" },
      { name: "Surface Raised", cssVar: "--color-surface-raised" },
      { name: "Surface Subtle", cssVar: "--color-surface-subtle" },
      { name: "Surface Muted", cssVar: "--color-surface-muted" },
      { name: "Surface Soft", cssVar: "--color-surface-soft" },
      { name: "Surface Soft Strong", cssVar: "--color-surface-soft-strong" },
      { name: "Surface Soft Contrast", cssVar: "--color-surface-soft-contrast" },
      { name: "Surface Accent 100", cssVar: "--color-surface-accent-100" },
      { name: "Surface Accent 200", cssVar: "--color-surface-accent-200" },
      { name: "Surface Accent 300", cssVar: "--color-surface-accent-300" },
      { name: "Surface Accent 400", cssVar: "--color-surface-accent-400" },
      { name: "Surface Accent 500", cssVar: "--color-surface-accent-500" },
      { name: "Border Subtle", cssVar: "--color-border-subtle" },
      { name: "Border Default", cssVar: "--color-border-default" },
      { name: "Border Strong", cssVar: "--color-border-strong" },
    ],
  },
  {
    title: "Action and Status",
    description: "Live tokens that drive button fills, links, and status accents.",
    tokens: [
      { name: "Action Primary", cssVar: "--color-action-primary" },
      { name: "Action Primary Hover", cssVar: "--color-action-primary-hover" },
      { name: "Action Primary Active", cssVar: "--color-action-primary-active" },
      { name: "Action Accent", cssVar: "--color-action-accent" },
      { name: "Action Accent Hover", cssVar: "--color-action-accent-hover" },
      { name: "Action Accent Active", cssVar: "--color-action-accent-active" },
      { name: "Success Soft", cssVar: "--color-status-success-soft" },
      { name: "Success", cssVar: "--color-status-success" },
      { name: "Success Strong", cssVar: "--color-status-success-strong" },
      { name: "Danger Soft", cssVar: "--color-status-danger-soft" },
      { name: "Danger", cssVar: "--color-status-danger" },
      { name: "Error", cssVar: "--color-status-error" },
      { name: "Decorative Accent", cssVar: "--color-decorative-accent" },
    ],
  },
]

const createDisplayStyle = (
  fontSize: string,
  lineHeight: string,
  extraStyles: CSSProperties = {}
): CSSProperties => ({
  margin: 0,
  fontFamily: "Denim INK, sans-serif",
  fontSize,
  lineHeight,
  fontWeight: 500,
  letterSpacing: "-0.02em",
  color: "var(--color-text-primary)",
  ...extraStyles,
})

const createTextStyle = (
  fontSize: string,
  lineHeight: string,
  fontWeight = 400,
  extraStyles: CSSProperties = {}
): CSSProperties => ({
  margin: 0,
  fontFamily: "Denim INK, sans-serif",
  fontSize,
  lineHeight,
  fontWeight,
  color: "var(--color-text-secondary)",
  ...extraStyles,
})

const createUiStyle = (
  fontSize: string,
  lineHeight: string,
  fontWeight = 500,
  extraStyles: CSSProperties = {}
): CSSProperties =>
  createTextStyle(fontSize, lineHeight, fontWeight, {
    color: "var(--color-text-primary)",
    ...extraStyles,
  })

const headingFeatureStyle: CSSProperties = {
  fontFeatureSettings: '"ss02" 1, "ss03" 1',
}

const DISPLAY_TYPOGRAPHY_SAMPLES: TypographySample[] = [
  {
    name: "Display Large",
    sourceLabel: "Figma desktop node",
    sample: "Launch with clarity",
    family: "Denim INK",
    desktop: "96px",
    mobile: "64px",
    lineHeight: "100%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    note: "Desktop node is active; the mobile frame omits Display, so the responsive minimum remains a raw-token fallback.",
    style: createDisplayStyle("clamp(6.4rem, 8vw, 9.6rem)", "1"),
  },
  {
    name: "Display Medium",
    sourceLabel: "Figma desktop node",
    sample: "Design system overview",
    family: "Denim INK",
    desktop: "72px",
    mobile: "48px",
    lineHeight: "100%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    note: "Desktop node is active; the mobile frame omits Display, so the responsive minimum remains a raw-token fallback.",
    style: createDisplayStyle("clamp(4.8rem, 6vw, 7.2rem)", "1"),
  },
  {
    name: "Display Small",
    sourceLabel: "Figma desktop node",
    sample: "Readable at a glance",
    family: "Denim INK",
    desktop: "56px",
    mobile: "40px",
    lineHeight: "110%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    note: "Desktop node is active; the mobile frame omits Display, so the responsive minimum remains a raw-token fallback.",
    style: createDisplayStyle("clamp(4rem, 5vw, 5.6rem)", "1.1"),
  },
]

const HEADING_TYPOGRAPHY_SAMPLES: TypographySample[] = [
  {
    name: "H1",
    sourceLabel: "Live global token",
    sample: "Heading level one",
    family: "Denim INK",
    desktop: "44px",
    mobile: "36px",
    lineHeight: "110%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    liveVars: ["--H1-FontSize", "--H1-LineHeight", "--H1-FontWeight"],
    style: createDisplayStyle(
      "var(--H1-FontSize)",
      "var(--H1-LineHeight)",
      headingFeatureStyle
    ),
  },
  {
    name: "H2",
    sourceLabel: "Live global token",
    sample: "Heading level two",
    family: "Denim INK",
    desktop: "40px",
    mobile: "30px",
    lineHeight: "110%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    liveVars: ["--H2-FontSize", "--H2-LineHeight", "--H2-FontWeight"],
    style: createDisplayStyle(
      "var(--H2-FontSize)",
      "var(--H2-LineHeight)",
      headingFeatureStyle
    ),
  },
  {
    name: "H3",
    sourceLabel: "Live global token",
    sample: "Heading level three",
    family: "Denim INK",
    desktop: "32px",
    mobile: "26px",
    lineHeight: "120%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    liveVars: ["--H3-FontSize", "--H3-LineHeight", "--H3-FontWeight"],
    style: createDisplayStyle(
      "var(--H3-FontSize)",
      "var(--H3-LineHeight)",
      headingFeatureStyle
    ),
  },
  {
    name: "H4",
    sourceLabel: "Live global token",
    sample: "Heading level four",
    family: "Denim INK",
    desktop: "26px",
    mobile: "24px",
    lineHeight: "120%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    liveVars: ["--H4-FontSize", "--H4-LineHeight", "--H4-FontWeight"],
    style: createDisplayStyle(
      "var(--H4-FontSize)",
      "var(--H4-LineHeight)",
      headingFeatureStyle
    ),
  },
  {
    name: "H5",
    sourceLabel: "Live global token",
    sample: "Heading level five",
    family: "Denim INK",
    desktop: "24px",
    mobile: "20px",
    lineHeight: "130%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    note: "Live HTML helper retained for compatibility; hidden in the current Figma typography frames.",
    liveVars: ["--H5-FontSize", "--H5-LineHeight", "--H5-FontWeight"],
    style: createDisplayStyle("var(--H5-FontSize)", "var(--H5-LineHeight)"),
  },
  {
    name: "H6",
    sourceLabel: "Live global token",
    sample: "Heading level six",
    family: "Denim INK",
    desktop: "20px",
    mobile: "16px",
    lineHeight: "140%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    note: "Live HTML helper retained for compatibility; hidden in the current Figma typography frames.",
    liveVars: ["--H6-FontSize", "--H6-LineHeight", "--H6-FontWeight"],
    style: createDisplayStyle("var(--H6-FontSize)", "var(--H6-LineHeight)"),
  },
  {
    name: "H7",
    sourceLabel: "Figma raw token",
    sample: "Heading level seven",
    family: "Denim INK",
    desktop: "16px",
    mobile: "14px",
    lineHeight: "130%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    note: "Hidden in the current Figma typography frames and not mapped to a shared HTML helper.",
    style: createDisplayStyle("clamp(1.4rem, 1.5vw, 1.6rem)", "1.3"),
  },
  {
    name: "H8",
    sourceLabel: "Figma raw token",
    sample: "Heading level eight",
    family: "Denim INK",
    desktop: "14px",
    mobile: "13px",
    lineHeight: "130%",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    note: "Hidden in the current Figma typography frames and not mapped to a shared HTML helper.",
    style: createDisplayStyle("clamp(1.3rem, 1.4vw, 1.4rem)", "1.3"),
  },
]

const TEXT_TYPOGRAPHY_SAMPLES: TypographySample[] = [
  {
    name: "Body Large",
    sourceLabel: "Figma active token",
    sample: "Larger narrative copy for compact highlight paragraphs and supporting intros.",
    family: "Denim INK",
    desktop: "18px",
    mobile: "16px",
    lineHeight: "150%",
    fontWeight: "400",
    style: createTextStyle("clamp(1.6rem, 1.25vw, 1.8rem)", "1.5"),
  },
  {
    name: "Body Large Bold",
    sourceLabel: "Figma active token",
    sample: "Larger semibold narrative copy for emphasized supporting content.",
    family: "Denim INK",
    desktop: "18px",
    mobile: "16px",
    lineHeight: "150%",
    fontWeight: "600",
    style: createTextStyle("clamp(1.6rem, 1.25vw, 1.8rem)", "1.5", 600),
  },
  {
    name: "Body Base",
    sourceLabel: "Live global token",
    sample: "Base body text used across pages, components, and shared content surfaces.",
    family: "Denim INK",
    desktop: "16px",
    mobile: "14px",
    lineHeight: "150%",
    fontWeight: "400",
    liveVars: ["--Text-FontSize", "--Text-LineHeight", "--Text-FontWeight"],
    style: createTextStyle("var(--Text-FontSize)", "var(--Text-LineHeight)", 400),
  },
  {
    name: "Body Base Bold",
    sourceLabel: "Figma active token",
    sample: "Semibold base body text for meaningful inline emphasis.",
    family: "Denim INK",
    desktop: "16px",
    mobile: "14px",
    lineHeight: "150%",
    fontWeight: "600",
    style: createTextStyle("var(--Text-FontSize)", "var(--Text-LineHeight)", 600),
  },
  {
    name: "Body Small",
    sourceLabel: "Figma active token",
    sample: "Smaller helper body copy for dense areas that still need readable prose.",
    family: "Denim INK",
    desktop: "14px",
    mobile: "12px",
    lineHeight: "150%",
    fontWeight: "400",
    style: createTextStyle("clamp(1.2rem, 0.9722222222vw, 1.4rem)", "1.5"),
  },
  {
    name: "Body Small Bold",
    sourceLabel: "Figma active token",
    sample: "Semibold helper copy for compact emphasized information.",
    family: "Denim INK",
    desktop: "14px",
    mobile: "12px",
    lineHeight: "150%",
    fontWeight: "600",
    style: createTextStyle("clamp(1.2rem, 0.9722222222vw, 1.4rem)", "1.5", 600),
  },
  {
    name: "Link Large",
    sourceLabel: "Figma raw token",
    sample: "Explore the design system",
    family: "Denim INK",
    desktop: "16px",
    mobile: "15px",
    lineHeight: "150%",
    fontWeight: "400",
    style: createTextStyle("clamp(1.5rem, 1.1111111111vw, 1.6rem)", "1.5", 400, {
      color: "var(--color-link-default)",
      textDecoration: "underline",
      textUnderlineOffset: "0.12em",
    }),
  },
  {
    name: "Link Base",
    sourceLabel: "Live link styling",
    sample: "Open source token map",
    family: "Denim INK",
    desktop: "14px",
    mobile: "13px",
    lineHeight: "150%",
    fontWeight: "400",
    style: createTextStyle("clamp(1.3rem, 0.9722222222vw, 1.4rem)", "1.5", 400, {
      color: "var(--color-link-default)",
      textDecoration: "underline",
      textUnderlineOffset: "0.12em",
    }),
  },
  {
    name: "Link Small",
    sourceLabel: "Figma raw token",
    sample: "Read details",
    family: "Denim INK",
    desktop: "12px",
    mobile: "11px",
    lineHeight: "150%",
    fontWeight: "400",
    style: createTextStyle("clamp(1.1rem, 0.8333333333vw, 1.2rem)", "1.5", 400, {
      color: "var(--color-link-default)",
      textDecoration: "underline",
      textUnderlineOffset: "0.12em",
    }),
  },
  {
    name: "Caption Large",
    sourceLabel: "Figma raw token",
    sample: "Caption for supporting metadata and short annotations.",
    family: "Denim INK",
    desktop: "13px",
    mobile: "12px",
    lineHeight: "140%",
    fontWeight: "400",
    style: createTextStyle("clamp(1.2rem, 0.9027777778vw, 1.3rem)", "1.4", 400, {
      color: "var(--color-text-secondary)",
    }),
  },
  {
    name: "Caption Base",
    sourceLabel: "Figma raw token",
    sample: "Caption base for compact details.",
    family: "Denim INK",
    desktop: "12px",
    mobile: "11px",
    lineHeight: "140%",
    fontWeight: "400",
    style: createTextStyle("clamp(1.1rem, 0.8333333333vw, 1.2rem)", "1.4", 400, {
      color: "var(--color-text-secondary)",
    }),
  },
  {
    name: "Caption Small",
    sourceLabel: "Figma raw token",
    sample: "Tightest caption size.",
    family: "Denim INK",
    desktop: "11px",
    mobile: "10px",
    lineHeight: "140%",
    fontWeight: "400",
    style: createTextStyle("clamp(1rem, 0.7638888889vw, 1.1rem)", "1.4", 400, {
      color: "var(--color-text-secondary)",
    }),
  },
]

const UI_TYPOGRAPHY_SAMPLES: TypographySample[] = [
  {
    name: "Button Large",
    sourceLabel: "Live button token",
    sample: "Primary action",
    family: "Denim INK",
    desktop: "16px",
    mobile: "14px",
    lineHeight: "120%",
    fontWeight: "500",
    note: "Button--lg converts to the medium button tier below 768px.",
    style: createUiStyle("1.6rem", "1.2"),
  },
  {
    name: "Button Base",
    sourceLabel: "Live button token",
    sample: "Primary action",
    family: "Denim INK",
    desktop: "14px",
    mobile: "12px",
    lineHeight: "120%",
    fontWeight: "500",
    note: "Button--md converts to the small button tier below 768px.",
    style: createUiStyle("1.4rem", "1.2"),
  },
  {
    name: "Button Small",
    sourceLabel: "Live button token",
    sample: "Primary action",
    family: "Denim INK",
    desktop: "12px",
    mobile: "12px",
    lineHeight: "120%",
    fontWeight: "500",
    note: "Button--sm stays on the small button tier at every breakpoint.",
    style: createUiStyle("1.2rem", "1.2"),
  },
  {
    name: "Button Tiny",
    sourceLabel: "Figma raw token",
    sample: "Compact action",
    family: "Denim INK",
    desktop: "11px",
    mobile: "10px",
    lineHeight: "120%",
    fontWeight: "500",
    note: "Token exists in Figma export, but the current button API stops at small.",
    style: createUiStyle("clamp(1rem, 0.7638888889vw, 1.1rem)", "1.2"),
  },
  {
    name: "Label Large",
    sourceLabel: "Figma desktop node",
    sample: "Field label",
    family: "Denim INK",
    desktop: "14px",
    mobile: "Not specified",
    lineHeight: "120%",
    fontWeight: "500",
    note: "The current mobile typography frame does not define label styles.",
    style: createUiStyle("1.4rem", "1.2"),
  },
  {
    name: "Label Base",
    sourceLabel: "Figma desktop node",
    sample: "Field label",
    family: "Denim INK",
    desktop: "12px",
    mobile: "Not specified",
    lineHeight: "120%",
    fontWeight: "500",
    note: "The current mobile typography frame does not define label styles.",
    style: createUiStyle("1.2rem", "1.2"),
  },
  {
    name: "Label Small",
    sourceLabel: "Figma desktop node",
    sample: "Field label",
    family: "Denim INK",
    desktop: "11px",
    mobile: "Not specified",
    lineHeight: "120%",
    fontWeight: "500",
    note: "The current mobile typography frame does not define label styles.",
    style: createUiStyle("1.1rem", "1.2"),
  },
  {
    name: "Code Large",
    sourceLabel: "Figma raw token",
    sample: "const token = '--color-button-fill-default'",
    family: "JetBrains Mono",
    desktop: "16px",
    mobile: "14px",
    lineHeight: "150%",
    fontWeight: "400",
    style: createUiStyle("clamp(1.4rem, 1.1111111111vw, 1.6rem)", "1.5", 400, {
      fontFamily: "JetBrains Mono, monospace",
    }),
  },
  {
    name: "Code Base",
    sourceLabel: "Live global code styling",
    sample: "const token = '--color-button-fill-default'",
    family: "JetBrains Mono",
    desktop: "14px",
    mobile: "13px",
    lineHeight: "150%",
    fontWeight: "400",
    style: createUiStyle("clamp(1.3rem, 0.9722222222vw, 1.4rem)", "1.5", 400, {
      fontFamily: "JetBrains Mono, monospace",
    }),
  },
  {
    name: "Code Small",
    sourceLabel: "Figma raw token",
    sample: "const token = '--color-button-fill-default'",
    family: "JetBrains Mono",
    desktop: "12px",
    mobile: "11px",
    lineHeight: "150%",
    fontWeight: "400",
    style: createUiStyle("clamp(1.1rem, 0.8333333333vw, 1.2rem)", "1.5", 400, {
      fontFamily: "JetBrains Mono, monospace",
    }),
  },
  {
    name: "Tag Base",
    sourceLabel: "Figma desktop node",
    sample: "Beta feature",
    family: "Denim INK",
    desktop: "11px",
    mobile: "Not specified",
    lineHeight: "120%",
    fontWeight: "500",
    letterSpacing: "0.02em",
    note: "The actual Figma Base node uses +2% tracking; its nearby annotation still says +8%.",
    style: createUiStyle("1.1rem", "1.2", 500, {
      textTransform: "uppercase",
      letterSpacing: "0.02em",
    }),
  },
  {
    name: "Tag Small",
    sourceLabel: "Figma desktop node",
    sample: "Beta feature",
    family: "Inter",
    desktop: "10px",
    mobile: "Not specified",
    lineHeight: "120%",
    fontWeight: "500",
    letterSpacing: "0.08em",
    note: "The current mobile typography frame does not define tag styles.",
    style: createUiStyle("1rem", "1.2", 500, {
      fontFamily: "Inter, sans-serif",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    }),
  },
]

const BUTTON_VARIANTS = [
  {
    key: "primary",
    label: "Primary",
    description: "Filled accent action.",
  },
  {
    key: "border",
    label: "Border",
    description: "Outlined action with accent stroke.",
  },
  {
    key: "danger",
    label: "Danger",
    description: "Destructive action with danger fill.",
  },
  {
    key: "ghost",
    label: "Ghost",
    description: "Subtle surface action with transparent border.",
  },
  {
    key: "underline",
    label: "Underline",
    description: "Textual action with underline treatment.",
  },
] as const

const BUTTON_THEMES = [
  {
    key: "default",
    label: "Default Theme",
    note: "Matches Figma default theme on light surfaces.",
    panelStyle: {
      ...panelStyle,
      display: "grid",
      gap: "2rem",
      backgroundColor: "var(--color-surface-default)",
      color: "var(--color-text-primary)",
    } as CSSProperties,
  },
  {
    key: "inverse",
    label: "Inverse Theme",
    note: "Matches Figma inverse theme on dark surfaces.",
    panelStyle: {
      ...panelStyle,
      display: "grid",
      gap: "2rem",
      background:
        "linear-gradient(135deg, var(--color-text-primary), #00062D)",
      border: "1px solid rgba(255, 255, 255, 0.12)",
      color: "var(--color-white)",
    } as CSSProperties,
  },
] as const

const BUTTON_SIZES = [
  { key: "large", label: "Large" },
  { key: "medium", label: "Medium" },
  { key: "small", label: "Small" },
] as const

const BUTTON_STATES = [
  { key: "enabled", label: "Default", disabled: false, forcedClass: "" },
  { key: "hover", label: "Hover", disabled: false, forcedClass: "is-hovered" },
  { key: "disabled", label: "Disabled", disabled: true, forcedClass: "" },
] as const

const FORM_PREVIEW_OPTIONS = "alpha;Alpha\r\nbeta;Beta\r\ngamma;Gamma"

const createFormElementOptions = (className = "") => ({
  formElementOptionsDisabled: false,
  formElementOptionsAutofocus: false,
  formElementOptionsAutocomplete: false,
  formElementOptionsMultiple: false,
  formElementOptionsStep: 0,
  formElementOptionsCssClass: className,
  formElementOptionsReadOnly: false,
})

const createFormValidation = (required = false) => ({
  formElementValidationRequired: required,
  formElementValidationPattern: undefined,
  formElementValidationMin: undefined,
  formElementValidationMax: undefined,
  formElementValidationMinLength: undefined,
  formElementValidationMaxLength: undefined,
})

const createFormElement = (
  name: string,
  label: string,
  placeholder = "",
  defaultValue = ""
) => ({
  formElementDefaultValue: defaultValue,
  formElementID: name,
  formElementLabel: label,
  formElementName: name,
  formElementPlaceholder: placeholder,
})

const createInputField = ({
  name,
  inputType = "text",
  label = "Field label",
  placeholder = "Placeholder",
  defaultValue = "",
  className = "",
  required = false,
}: {
  name: string
  inputType?: string
  label?: string
  placeholder?: string
  defaultValue?: string
  className?: string
  required?: boolean
}): FormFieldInputType => ({
  displayText: label,
  contentType: "PuxFormFieldInput",
  inputType,
  metadata: { alignment: "", size: 100 },
  puxFormElement: createFormElement(name, label, placeholder, defaultValue),
  puxFormElementOptions: createFormElementOptions(className),
  puxFormElementValidation: createFormValidation(required),
})

const createTextareaField = ({
  name,
  defaultValue = "",
  className = "",
}: {
  name: string
  defaultValue?: string
  className?: string
}): FormFieldTextareaType => ({
  displayText: "Field label",
  contentType: "PuxFormFieldTextarea",
  metadata: { alignment: "", size: 100 },
  puxFormElement: createFormElement(
    name,
    "Field label",
    "Placeholder",
    defaultValue
  ),
  puxFormElementOptions: createFormElementOptions(className),
  puxFormElementValidation: createFormValidation(false),
  textareaResize: false,
  textareaRows: 4,
})

const createSelectField = ({
  name,
  defaultValue = "",
  className = "",
}: {
  name: string
  defaultValue?: string
  className?: string
}): FormFieldMultipleType => ({
  displayText: "Field label",
  contentType: "PuxFormFieldMultiple",
  metadata: { alignment: "", size: 100 },
  multipleOptions: FORM_PREVIEW_OPTIONS,
  multipleType: "select",
  puxFormElement: createFormElement(name, "Field label", "Placeholder", defaultValue),
  puxFormElementOptions: createFormElementOptions(className),
  puxFormElementValidation: createFormValidation(false),
})

const createRadioField = (name: string, className = ""): FormFieldMultipleType => ({
  displayText: "Radio button",
  contentType: "PuxFormFieldMultiple",
  metadata: { alignment: "", size: 100 },
  multipleOptions: "no;No\r\nyes;Yes",
  multipleType: "radio",
  puxFormElement: createFormElement(name, "Radio button", "", "yes"),
  puxFormElementOptions: createFormElementOptions(className),
  puxFormElementValidation: createFormValidation(false),
})

const TRACKED_CSS_VARS = Array.from(
  new Set([
    ...SEMANTIC_COLOR_SECTIONS.flatMap((section) =>
      section.tokens.map((token) => token.cssVar).filter(Boolean)
    ),
    "--H1-FontSize",
    "--H1-LineHeight",
    "--H1-FontWeight",
    "--H2-FontSize",
    "--H2-LineHeight",
    "--H2-FontWeight",
    "--H3-FontSize",
    "--H3-LineHeight",
    "--H3-FontWeight",
    "--H4-FontSize",
    "--H4-LineHeight",
    "--H4-FontWeight",
    "--H5-FontSize",
    "--H5-LineHeight",
    "--H5-FontWeight",
    "--H6-FontSize",
    "--H6-LineHeight",
    "--H6-FontWeight",
    "--Text-FontSize",
    "--Text-LineHeight",
    "--Text-FontWeight",
  ])
) as string[]

const createPreviewLink = (label: string) => ({
  url: [""] as [string],
  internal: [false] as [boolean],
  text: [label] as [string],
})

const createButtonPreviewClassName = (
  state: (typeof BUTTON_STATES)[number]
) => state.forcedClass

const resolveCssValue = (
  cssValues: Record<string, string>,
  cssVar?: string
): string => {
  if (!cssVar) {
    return ""
  }

  return cssValues[cssVar] || `var(${cssVar})`
}

const renderSwatchSection = (
  section: SwatchSection,
  cssValues: Record<string, string>
) => (
  <div style={{ display: "grid", gap: "1.6rem" }} key={section.title}>
    <div style={{ display: "grid", gap: "0.4rem" }}>
      <h3 style={{ margin: 0 }}>{section.title}</h3>
      <p style={textMetaStyle}>{section.description}</p>
    </div>
    <div style={swatchGridStyle}>
      {section.tokens.map((token) => (
        <div style={swatchCardStyle} key={`${section.title}-${token.name}`}>
          <div
            style={{
              height: "8.4rem",
              borderRadius: "1.2rem",
              backgroundColor: token.value || `var(${token.cssVar})`,
              border: "1px solid rgba(0, 17, 135, 0.10)",
            }}
          />
          <div style={{ display: "grid", gap: "0.4rem" }}>
            <strong style={{ fontSize: "1.4rem", lineHeight: 1.3 }}>
              {token.name}
            </strong>
            <p style={subtleTextStyle}>{token.cssVar || token.note || "Raw token"}</p>
            <p style={{ ...textMetaStyle, color: "var(--color-text-primary)" }}>
              {token.value || resolveCssValue(cssValues, token.cssVar)}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
)

const renderTypographySection = (
  title: string,
  description: string,
  samples: TypographySample[],
  cssValues: Record<string, string>
) => (
  <div style={{ display: "grid", gap: "1.6rem" }}>
    <div style={{ display: "grid", gap: "0.4rem" }}>
      <h3 style={{ margin: 0 }}>{title}</h3>
      <p style={textMetaStyle}>{description}</p>
    </div>
    <div style={typographyGridStyle}>
      {samples.map((sample) => (
        <div style={typeCardStyle} key={sample.name}>
          <div style={{ display: "grid", gap: "0.8rem" }}>
            <span style={badgeStyle}>{sample.sourceLabel}</span>
            <div style={{ display: "grid", gap: "0.4rem" }}>
              <strong style={{ fontSize: "1.6rem", lineHeight: 1.3 }}>
                {sample.name}
              </strong>
              <p style={textMetaStyle}>{sample.note || `${sample.family} family`}</p>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              alignItems: "center",
              minHeight: "10rem",
              padding: "1.6rem",
              borderRadius: "1.2rem",
              border: "1px solid var(--color-border-subtle)",
              backgroundColor: "var(--color-surface-raised)",
            }}
          >
            <div style={sample.style}>{sample.sample}</div>
          </div>
          <div style={{ display: "grid", gap: "0.6rem" }}>
            <p style={subtleTextStyle}>{`Desktop ${sample.desktop} | Mobile ${sample.mobile}`}</p>
            <p style={subtleTextStyle}>{`Line height ${sample.lineHeight} | Weight ${sample.fontWeight}${sample.letterSpacing ? ` | Tracking ${sample.letterSpacing}` : ""}`}</p>
            {sample.liveVars ? (
              <p style={subtleTextStyle}>
                {`Live vars ${sample.liveVars
                  .map((cssVar) => `${cssVar}: ${resolveCssValue(cssValues, cssVar)}`)
                  .join(" | ")}`}
              </p>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  </div>
)

const StyleGuideFormPreviewFields: FunctionComponent = () => {
  const methods = useForm()
  const { setError } = methods

  useEffect(() => {
    setError("styleguideDefaultTextError", {
      type: "manual",
      message: "This text field is required",
    })
    setError("styleguideInverseTextError", {
      type: "manual",
      message: "This text field is required",
    })
  }, [setError])

  const renderTheme = (theme: "default" | "inverse") => {
    const inverse = theme === "inverse"
    const groupThemeClass = inverse ? "FormGroup--inverse" : ""
    const themeLabel = inverse ? "Inverse" : "Default"
    const themeNote = inverse
      ? "Dark-surface controls with inverse labels, borders, icons, and error feedback."
      : "Default controls on a light surface, mapped to the shared semantic tokens."

    return (
      <div
        className={inverse ? "FormTheme--inverse" : undefined}
        style={formThemePanelStyle(inverse)}
      >
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <h3
            style={{
              margin: 0,
              color: inverse ? "var(--color-white)" : "var(--color-text-primary)",
            }}
          >
            {themeLabel}
          </h3>
          <p
            style={{
              ...textMetaStyle,
              color: inverse
                ? "rgba(255, 255, 255, 0.78)"
                : "var(--color-text-secondary)",
            }}
          >
            {themeNote}
          </p>
        </div>

        <div style={{ display: "grid", gap: "2rem" }}>
          <div style={{ display: "grid", gap: "1rem" }}>
            <strong
              style={{
                color: inverse ? "var(--color-white)" : "var(--color-text-primary)",
              }}
            >
              Text input
            </strong>
            <div style={formPreviewGridStyle}>
              <FormFieldInputWidget
                props={createInputField({
                  name: `styleguide${themeLabel}TextDefault`,
                  className: `${groupThemeClass} FormGroup--withIcon`,
                })}
              />
              <FormFieldInputWidget
                props={createInputField({
                  name: `styleguide${themeLabel}TextHover`,
                  placeholder: "Typing...",
                  className: `${groupThemeClass} FormGroup--hover FormGroup--withIcon`,
                })}
              />
              <FormFieldInputWidget
                props={createInputField({
                  name: `styleguide${themeLabel}TextFilled`,
                  defaultValue: "Value",
                  className: `${groupThemeClass} FormGroup--filled FormGroup--withIcon`,
                })}
              />
              <FormFieldInputWidget
                props={createInputField({
                  name:
                    theme === "inverse"
                      ? "styleguideInverseTextError"
                      : "styleguideDefaultTextError",
                  className: `${groupThemeClass} FormGroup--withIcon`,
                  required: true,
                })}
              />
            </div>
          </div>

          <div style={{ display: "grid", gap: "1rem" }}>
            <strong
              style={{
                color: inverse ? "var(--color-white)" : "var(--color-text-primary)",
              }}
            >
              Textarea and dropdown
            </strong>
            <div style={formPreviewGridStyle}>
              <FormFieldTextareaWidget
                props={createTextareaField({
                  name: `styleguide${themeLabel}TextareaDefault`,
                  className: groupThemeClass,
                })}
              />
              <FormFieldTextareaWidget
                props={createTextareaField({
                  name: `styleguide${themeLabel}TextareaFilled`,
                  defaultValue: "Value",
                  className: `${groupThemeClass} FormGroup--filled`,
                })}
              />
              <FormFieldMultipleWidget
                props={createSelectField({
                  name: `styleguide${themeLabel}SelectDefault`,
                  className: groupThemeClass,
                })}
              />
              <FormFieldMultipleWidget
                props={createSelectField({
                  name: `styleguide${themeLabel}SelectFilled`,
                  defaultValue: "beta",
                  className: `${groupThemeClass} FormGroup--filled`,
                })}
              />
            </div>
          </div>

          <div style={{ display: "grid", gap: "1rem" }}>
            <strong
              style={{
                color: inverse ? "var(--color-white)" : "var(--color-text-primary)",
              }}
            >
              Checkbox and radio
            </strong>
            <div style={formPreviewGridStyle}>
              <FormFieldCheckboxWidget
                props={createInputField({
                  name: `styleguide${themeLabel}CheckboxUnchecked`,
                  inputType: "checkbox",
                  label: "Checkbox",
                  className: groupThemeClass,
                })}
              />
              <FormFieldCheckboxWidget
                props={createInputField({
                  name: `styleguide${themeLabel}CheckboxChecked`,
                  inputType: "checkbox",
                  label: "Checkbox",
                  defaultValue: "checked",
                  className: groupThemeClass,
                })}
              />
              <FormFieldRadioWidget
                props={createRadioField(
                  `styleguide${themeLabel}Radio`,
                  groupThemeClass
                )}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      <div style={{ display: "grid", gap: "2.4rem" }}>
        {renderTheme("default")}
        {renderTheme("inverse")}
      </div>
    </FormProvider>
  )
}

const StyleGuideFormPreview: FunctionComponent = () => {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) {
    return (
      <div style={panelStyle}>
        <p style={textMetaStyle}>Loading shared form controls...</p>
      </div>
    )
  }

  return <StyleGuideFormPreviewFields />
}

const StyleGuide: FunctionComponent = () => {
  const [cssValues, setCssValues] = useState<Record<string, string>>({})

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined
    }

    const syncCssValues = () => {
      const rootStyles = window.getComputedStyle(document.documentElement)
      const nextValues: Record<string, string> = {}

      TRACKED_CSS_VARS.forEach((cssVar) => {
        nextValues[cssVar] = rootStyles.getPropertyValue(cssVar).trim()
      })

      setCssValues(nextValues)
    }

    syncCssValues()
    window.addEventListener("resize", syncCssValues)

    return () => window.removeEventListener("resize", syncCssValues)
  }, [])

  return (
    <main style={pageStyle}>
      <section style={{ ...sectionStyle, paddingTop: "4rem" }}>
        <div className="Container">
          <div style={heroPanelStyle}>
            <div style={{ display: "grid", gap: "1.6rem" }}>
              <span style={eyebrowStyle}>Easy Software Redesign</span>
              <div style={{ display: "grid", gap: "1.2rem" }}>
                <h1 style={{ margin: 0 }}>Figma Styleguide</h1>
                <p
                  style={{
                    margin: 0,
                    maxWidth: "72rem",
                    fontSize: "1.8rem",
                    lineHeight: 1.6,
                    color: "var(--color-text-secondary)",
                  }}
                >
                  This page renders the design-system surface currently wired from
                  Figma: raw palette tokens, exported semantic CSS variables,
                  typography scales, the canonical button API, and shared form
                  controls. Legacy button classes and pre-redesign examples are
                  intentionally gone.
                </p>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1rem",
                marginTop: "2.4rem",
              }}
            >
              <span style={badgeStyle}>Exported 2026-05-05</span>
              <span style={badgeStyle}>Colors node 8116:33025</span>
              <span style={badgeStyle}>Typography node 8116:32886</span>
              <span style={badgeStyle}>Buttons node 8116:33664</span>
              <span style={badgeStyle}>Forms node 8178:52444</span>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1.6rem",
                marginTop: "2.4rem",
              }}
            >
              <a href="#colors" style={previewLinkStyle}>
                Colors
              </a>
              <a href="#typography" style={previewLinkStyle}>
                Typography
              </a>
              <a href="#buttons" style={previewLinkStyle}>
                Buttons
              </a>
              <a href="#forms" style={previewLinkStyle}>
                Forms
              </a>
              <a href={FIGMA_FILE_URL} style={previewLinkStyle}>
                Open Figma file
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="colors" style={sectionStyle}>
        <div className="Container">
          <div style={sectionHeaderStyle}>
            <span style={eyebrowStyle}>Colors</span>
            <h2 style={{ margin: 0 }}>Raw palette and live semantic tokens</h2>
            <p style={textMetaStyle}>
              Raw palette swatches mirror the exported Figma variables. Semantic
              swatches below them are the live CSS custom properties currently
              exported from :root and consumed by the implementation.
            </p>
          </div>
          <div style={{ display: "grid", gap: "2.4rem" }}>
            <div style={panelStyle}>
              <div style={{ display: "grid", gap: "2.4rem" }}>
                {RAW_PALETTE_SECTIONS.map((section) =>
                  renderSwatchSection(section, cssValues)
                )}
              </div>
            </div>
            <div style={panelStyle}>
              <div style={{ display: "grid", gap: "2.4rem" }}>
                {SEMANTIC_COLOR_SECTIONS.map((section) =>
                  renderSwatchSection(section, cssValues)
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="typography" style={sectionStyle}>
        <div className="Container">
          <div style={sectionHeaderStyle}>
            <span style={eyebrowStyle}>Typography</span>
            <h2 style={{ margin: 0 }}>Display, heading, body, and UI scales</h2>
            <p style={textMetaStyle}>
              This section combines live global typography tokens with raw Figma
              typography exports that are available but not yet bound to shared
              HTML helpers. Denim INK Regular, Medium, and SemiBold are all loaded
              locally; semantic bold text uses the Figma 600 weight.
            </p>
          </div>
          <div style={{ display: "grid", gap: "2.4rem" }}>
            <div style={panelStyle}>
              {renderTypographySection(
                "Display",
                "Desktop Display styles from Figma. The current mobile frame omits this group, so mobile values remain documented raw-token fallbacks.",
                DISPLAY_TYPOGRAPHY_SAMPLES,
                cssValues
              )}
            </div>
            <div style={panelStyle}>
              {renderTypographySection(
                "Headings",
                "H1-H4 match the active Desktop and Mobile frames. H5-H8 are retained for compatibility but are hidden in the current Figma typography board.",
                HEADING_TYPOGRAPHY_SAMPLES,
                cssValues
              )}
            </div>
            <div style={panelStyle}>
              {renderTypographySection(
                "Body and Content",
                "Active regular and semibold Body tiers plus the supporting link and caption raw-token scales.",
                TEXT_TYPOGRAPHY_SAMPLES,
                cssValues
              )}
            </div>
            <div style={panelStyle}>
              {renderTypographySection(
                "UI and Utility",
                "Live button labels, desktop Figma labels and tags, and the supporting raw-token code scale.",
                UI_TYPOGRAPHY_SAMPLES,
                cssValues
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="forms" style={sectionStyle}>
        <div className="Container">
          <div style={sectionHeaderStyle}>
            <span style={eyebrowStyle}>Forms</span>
            <h2 style={{ margin: 0 }}>Shared PuxForm controls</h2>
            <p style={textMetaStyle}>
              These previews reuse the existing CMS-shaped PuxForm field
              components and the shared global form styles. The same classes and
              tokens are available outside the styleguide for plain site forms,
              Mautic rows, and CMS-rendered form widgets.
            </p>
          </div>
          <StyleGuideFormPreview />
        </div>
      </section>

      <section id="buttons" style={sectionStyle}>
        <div className="Container">
          <div style={sectionHeaderStyle}>
            <span style={eyebrowStyle}>Buttons</span>
            <h2 style={{ margin: 0 }}>Canonical PuxButton matrix</h2>
            <p style={textMetaStyle}>
              This matrix shows the canonical post-migration button API:
              variants Primary, Border, Danger, Ghost, and Underline; themes
              Default and Inverse; sizes Large, Medium, and Small; and explicit
              hover and disabled states. Below 768px, Large converts to Medium,
              Medium converts to Small, and Small remains Small. Project-only
              helpers such as success and warn are intentionally excluded
              because they are not part of the Figma button set.
            </p>
          </div>
          <div style={{ display: "grid", gap: "2.4rem" }}>
            {BUTTON_THEMES.map((theme) => (
              <div key={theme.key} style={theme.panelStyle}>
                <div style={{ display: "grid", gap: "0.6rem" }}>
                  <h3
                    style={{
                      margin: 0,
                      color:
                        theme.key === "inverse"
                          ? "var(--color-white)"
                          : "var(--color-text-primary)",
                    }}
                  >
                    {theme.label}
                  </h3>
                  <p
                    style={{
                      ...textMetaStyle,
                      color:
                        theme.key === "inverse"
                          ? "rgba(255, 255, 255, 0.80)"
                          : "var(--color-text-secondary)",
                    }}
                  >
                    {theme.note}
                  </p>
                </div>
                <div style={{ display: "grid", gap: "1.6rem" }}>
                  {BUTTON_VARIANTS.map((variant) => (
                    <div
                      key={`${theme.key}-${variant.key}`}
                      style={{
                        ...buttonVariantCardStyle,
                        border:
                          theme.key === "inverse"
                            ? "1px solid rgba(255, 255, 255, 0.12)"
                            : "1px solid var(--color-border-subtle)",
                        backgroundColor:
                          theme.key === "inverse"
                            ? "rgba(255, 255, 255, 0.04)"
                            : "var(--color-surface-raised)",
                      }}
                    >
                      <div style={{ display: "grid", gap: "0.4rem" }}>
                        <strong style={{ fontSize: "1.6rem", lineHeight: 1.3 }}>
                          {variant.label}
                        </strong>
                        <p
                          style={{
                            ...subtleTextStyle,
                            color:
                              theme.key === "inverse"
                                ? "rgba(255, 255, 255, 0.70)"
                                : "var(--color-text-muted)",
                          }}
                        >
                          {variant.description}
                        </p>
                      </div>
                      <div style={buttonGridStyle}>
                        {BUTTON_STATES.flatMap((state) =>
                          BUTTON_SIZES.map((size) => (
                            <div
                              key={`${theme.key}-${variant.key}-${state.key}-${size.key}`}
                              style={{
                                ...buttonCellStyle,
                                backgroundColor:
                                  theme.key === "inverse"
                                    ? "rgba(255, 255, 255, 0.05)"
                                    : "var(--color-surface-subtle)",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "1.2rem",
                                  lineHeight: 1.3,
                                  color:
                                    theme.key === "inverse"
                                      ? "rgba(255, 255, 255, 0.74)"
                                      : "var(--color-text-secondary)",
                                }}
                              >
                                {`${size.label} · ${state.label}`}
                              </span>
                              <PuxButton
                                buttonType={variant.key}
                                buttonTheme={theme.key}
                                buttonSize={size.key}
                                buttonDisabled={state.disabled}
                                buttonIcon="arrows/arrow-right"
                                buttonClassName={createButtonPreviewClassName(state)}
                                buttonLink={createPreviewLink(variant.label)}
                              />
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  )
}

export default StyleGuide

export const Head = () => <title>Figma Styleguide</title>
